"use client";
import { checkIsDark, createColorRGB } from "@/lib/color";
import { cn } from "@/lib/utils";
import { forwardRef, useMemo } from "react";

export const LinkComponentStyle = (props) => {
  return (
    <div
      {...props}
      className={cn("relative flex flex-col w-full p-2 ", props.className)}
    />
  );
};

export const StyledLink = forwardRef<any, any>(function StyledLink(
  {
    buttonType,
    buttonEffect,
    buttonColor,
    className,
    isButton = false,
    hasButtonShadow,
    buttonBorderColor,
    ...props
  },
  ref
) {
  const style = useMemo(() => {
    const styles: any = props.style ? props.style : {};
    if (buttonColor) {
      styles["--link-background"] = createColorRGB(buttonColor);
      styles["--link-color"] = createColorRGB(
        checkIsDark(buttonColor || "#fff") ? "#fff" : "#333"
      );
    }
    return styles;
  }, [buttonColor, props.style]);

  const linkClasses = cn(
    // Base link styles
    "px-1.5 py-1.5",
    "relative flex items-center w-full text-left transition-all duration-200",
    "bg-[rgb(var(--link-background))] text-[rgb(var(--link-color))]",
    // Button type variants
    buttonType === "elevate" && [
      "rounded-lg shadow-lg hover:shadow-xl",
      "hover:scale-[1.02] active:scale-[0.98]",
    ],
    buttonType === "solid" && ["rounded-lg"],
    buttonType === "outline" && [
      "bg-[rgb(var(--link-background))]/40",
      "rounded-lg bg-opacity-50",
      "border-2",
    ],
    buttonType === "square" && [],
    buttonType === "full-round" && [
      "rounded-full",
      buttonBorderColor ? `border-2 border-[rgb(var(--link-border))]` : "",
      hasButtonShadow
        ? "mb-1 shadow-[0_0.5rem_0_0px_rgb(var(--link-shadow))] hover:shadow-[0_0_0_0_rgb(var(--link-shadow))] hover:translate-y-[0.5rem]"
        : "shadow-sm hover:shadow-md",
    ],

    className
  );

  const outerClasses = cn(
    "relative",
    // Button effects
    buttonEffect === "shake" &&
      "animate-[buttonShake_1.5s_ease-in-out_infinite] hover:animate-none",
    buttonEffect === "bounce" &&
      "animate-[buttonBounce_1.5s_ease-in-out_infinite] hover:animate-none"
  );

  return (
    <div className={outerClasses}>
      {isButton ? (
        <button {...props} ref={ref} style={style} className={linkClasses} />
      ) : (
        <a {...props} ref={ref} style={style} className={linkClasses} />
      )}
    </div>
  );
});

export const StyledIcon = ({ buttonType, className = "", ...props }) => {
  return (
    <div
      {...props}
      className={cn(
        "flex items-center justify-center shrink-0",
        "overflow-hidden",
        "size-(--icon-width) text-lg",
        "bg-black/20",
        buttonType === "full-round"
          ? "rounded-full"
          : buttonType === "square"
          ? ""
          : "rounded-md",
        className
      )}
    />
  );
};

// Dummy component for spacing
export const DummyIcon = ({
  className,
  left,
  right,
  textPosition,
}: {
  className?: string;
  left?: boolean;
  right?: boolean;
  textPosition?: string;
}) => {
  return (
    <div
      className={cn(
        "size-(--icon-width) shrink-0",
        left ? (!textPosition || textPosition === "left" ? "hidden" : "") : "",
        right ? (textPosition === "right" ? "hidden" : "") : ""
      )}
    />
  );
};

// Text outer container
export const TextOuter = ({ className, ...props }) => {
  return <div {...props} className={cn("flex-1 px-3 min-w-0", className)} />;
};

// Text component with positioning
export const StyledText = ({ position = "left", className, ...props }) => {
  return (
    <div
      {...props}
      className={cn(
        "w-full truncate",
        position === "left" && "text-left",
        position === "center" && "text-center",
        position === "right" && "text-right",
        position === "justify" && "text-justify",
        className
      )}
    />
  );
};
